import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import { sortTimeSlots, isSlotPassed, formatTimeSlotRange, parseTimeSlotStartHour, formatHourLabel, getCourtLabel, getTimeSlotsForDate, getTimeSlotSetForDate, describeSlotSetDays } from '../utils/bookingUtils';
import { getPhToday } from '../utils/phTime';
import { buildBookedSlotsFromSnapshot } from '../utils/bookingAvailabilityUtil';
import {
  saveOpenPlaySession,
  subscribeOpenPlaySessions,
  subscribeSessionJoins,
  addManualOpenPlayJoin,
  removeOpenPlayPlayers,
  nextDateStr,
  getSessionSlotsForDate,
} from '../utils/openPlayUtil';
import { OPEN_PLAY_NEXT_DAY_END_HOUR } from '../constants/bookingConst';
import NumberComboField from './NumberComboField';
import '../css/AdminHostOpenPlayModal.css';
import { getCurrencySymbol } from '../utils/currencyUtil';

const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const formatDateLabel = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// 10, 20, 30 … 100. Open Play sessions are club-wide rotations rather than
// per-court games, so caps land on round tens in practice.
const MAX_PLAYERS_OPTIONS = Array.from({ length: 10 }, (_, i) => (i + 1) * 10);

/**
 * Admin "Host an Open Play session" — creates OR edits a whole Open Play
 * SESSION at once (one or more courts, each with its own set of hours),
 * rather than flipping individual grid cells one at a time. The admin picks
 * a date, taps a court to open a small "Court N — set the time" popup
 * (defaults to a Time start / Time end range picker — see
 * openCourtPicker/renderCourtPicker below — with a link to switch to
 * picking individual hour chips manually instead), a player cap, a fee, and
 * optional notes, then Confirm applies it all as a single atomic Firebase
 * write (see saveOpenPlaySession in openPlayUtil.js):
 *   - a slotOverrides/{date}/{slot}/{court} = 'openPlay' entry for every
 *     chosen (court, slot) pair — the exact same mechanism the old per-cell
 *     dropdown used, so TimeSlotSelection.jsx's guest-facing "Open Play
 *     (Click to join)" button picks these up with zero extra guest-side work.
 *   - one openPlaySessions/{id} record tying those cells together with the
 *     cap/fee/notes, which OpenPlayModal.jsx and OpenPlaySessionCard.jsx read.
 * Opened either from the grid's "Host an Open Play session" button (create
 * mode, no `session` prop) or from clicking an existing session's card in
 * OpenPlaySessionsSection (edit mode, `session` prop set) — see
 * AdminTimeSlotSelection.jsx.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {string} sportId - which sport's courts to offer (the admin
 *   grid's currently active sport tab) — ignored when `session` is set,
 *   since an existing session's own sportId always wins.
 * @param {Date} initialDate - defaults the date picker to the grid's
 *   currently-viewed date (ignored when `session` is set — its own date wins)
 * @param {object|null} session - an existing openPlaySessions record (with
 *   its `id`) to edit in place, opened from clicking its card in
 *   OpenPlaySessionsSection. Omit/null to host a brand new session.
 * @param {(mode: 'create'|'edit') => void} onHosted - called after a successful Confirm
 */
function AdminHostOpenPlayModal({ isOpen, onClose, sportId, initialDate, session = null, onHosted }) {
  const { config } = useConfig();
  // Editing an existing session always uses ITS OWN sport, regardless of
  // which sport tab happens to be active on the admin grid right now (the
  // admin could be viewing Tennis but click a hosted Pickleball session's
  // card) — only a brand new session (no `session` prop) uses the grid's
  // active tab.
  const effectiveSportId = session?.sportId || sportId;
  const sport = (config?.sports || []).find(s => s.id === effectiveSportId);
  const sportName = sport?.name || 'the';
  const courts = (config?.courts || [])
    .filter(c => c.sportId === effectiveSportId)
    .map(c => Number(c.number))
    .sort((a, b) => a - b);

  const parseDateStr = (str) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // ---- Date floor: Open Play can only be hosted for today onwards --------
  //
  // A session on a past date can never be joined, and the slotOverrides it
  // writes land on a day the grid already paints as "passed" — so it is a
  // silent no-op that still looks like a hosted session in the admin's list.
  // The admin grid's own date picker is pinned the same way (`min={dateKey
  // (today)}` in AdminTimeSlotSelection.jsx); this modal simply wasn't.
  //
  // Recomputed each time the modal OPENS rather than once at mount, because
  // the admin dashboard is a tab people leave open — mounted once, reopened
  // for days — and a `useMemo(..., [])` would keep insisting it is still
  // whatever day the tab was first loaded on.
  const today = useMemo(() => getPhToday(), [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const todayKey = dateKey(today);
  // EXCEPTION — a session already hosted for a past date keeps its own date
  // as the floor instead of today. Editing one is still legitimate (fixing
  // the player list, correcting a fee, deleting it), and forcing its date
  // forward on open would silently move a finished session onto today's
  // grid the moment the admin pressed Save. It just can't go back further.
  const minDateKey = session?.date && session.date < todayKey ? session.date : todayKey;
  // Anything earlier than the floor collapses to the floor rather than being
  // rejected outright, so a stale `initialDate` (the grid's selected day,
  // days after it was chosen) opens on a usable date instead of an invalid
  // one the admin then has to notice and fix.
  const clampDate = (d) => (d && dateKey(d) >= minDateKey ? d : today);

  const [date, setDate] = useState(() => clampDate(initialDate));

  // Opening hours can differ by weekday (Config -> Sports & Courts -> Time
  // Slots), so BOTH sides of the timeline are resolved against their own
  // date. A Friday-night session running into Saturday morning is offered
  // Friday's hours before midnight and SATURDAY's after it — which is the
  // whole point of a club that closes earlier at weekends.
  const sortedSlots = useMemo(
    () => getTimeSlotsForDate(config, dateKey(date)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets, date],
  );
  const nextDaySlots = useMemo(
    () => getTimeSlotsForDate(config, nextDateStr(dateKey(date))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets, date],
  );

  // ---- The timeline: this night, continued into the next morning ---------
  //
  // A session may run past midnight ("10PM - 6AM"), so the hours it can be
  // built from are not one calendar day's slot list but a SEQUENCE that
  // starts at the selected date's first hour and carries on into the next
  // date's early hours (up to OPEN_PLAY_NEXT_DAY_END_HOUR).
  //
  // Each entry is one pickable hour:
  //   { slot, dayOffset, hour, abs }
  // where `abs` is the hour on a timeline anchored to the selected date —
  // 22 is 10PM tonight, 26 is 2AM tomorrow. Everything downstream (the range
  // dropdowns, the slice that turns a start/end pair into hours, the summary
  // line) works on POSITIONS IN THIS ARRAY, which is what lets a range cross
  // midnight without a single special case.
  //
  // Only hours that genuinely exist in Config -> Time Slots are offered: a
  // next-day hour with no configured slot has no row on any grid, so an
  // override written to it would be invisible to guests and admins alike.
  // The NEXT-DAY tail is therefore cut at the first discontinuity — a club
  // whose day ends at 11PM and has no 12-1AM slot gets exactly the picker it
  // had before, rather than a broken jump from midnight to 6AM.
  const timeline = useMemo(() => {
    if (sortedSlots.length === 0) return [];

    // THE SELECTED DATE'S OWN HOURS ARE ALWAYS INCLUDED WHOLE, gaps and all.
    // A Time Slots list is not required to be contiguous — a club closed
    // 12-1PM for lunch, or one that has just added 12-1AM without the hours
    // between, is an ordinary list — and cutting the day at its first gap
    // leaves the picker offering a single hour. (It did exactly that: a list
    // holding 12-1AM and then nothing until 6AM produced a Time start
    // dropdown whose only option was 12:00 MN.) Gaps within the day behave
    // as they always have; only the overnight extension is new.
    const today = sortedSlots.map(slot => {
      const hour = parseTimeSlotStartHour(slot);
      return { slot, dayOffset: 0, hour, abs: hour };
    });

    // Tomorrow extends the day only while each hour follows the last without
    // a break. Contiguity matters HERE and not above because the range
    // picker slices this array: a hole in the overnight tail would hand a
    // session hours it never selected. Reaching 6AM therefore needs every
    // hour from the day's last slot through to 6AM present in the config.
    const out = [...today];
    nextDaySlots
      .map((slot) => {
        const hour = parseTimeSlotStartHour(slot);
        return { slot, dayOffset: 1, hour, abs: hour + 24 };
      })
      .filter(e => e.hour < OPEN_PLAY_NEXT_DAY_END_HOUR)
      .forEach((entry) => {
        if (entry.abs === out[out.length - 1].abs + 1) out.push(entry);
      });
    return out;
  }, [sortedSlots, nextDaySlots]);

  /**
   * The slot LABEL an hour would be typed as in Config -> Time Slots, using
   * the app's own convention: the suffix is the meridiem of the hour it ENDS
   * on, which is why 11PM-midnight is written "11-12AM" and not "11-12PM".
   * @param {number} hour 0-23
   */
  const slotLabelForHour = (hour) => {
    const h12 = (x) => (x % 12 === 0 ? 12 : x % 12);
    const end = (hour + 1) % 24;
    return `${h12(hour)}-${h12(end)}${end === 0 || end < 12 ? 'AM' : 'PM'}`;
  };

  // WHY the picker stops where it does, when it stops short of the overnight
  // cap — the exact hour that is missing and which day's list needs it.
  //
  // A missing hour is a config gap, not a bug, but silence about it is: the
  // admin sees a Time end dropdown that stops at midnight with nothing
  // saying why. It used to go quiet in precisely the case that matters —
  // the club had put its small hours on the day it was HOSTING but the next
  // day had none, so there were no unreachable next-day hours to notice.
  //
  // Only shown when the club has small hours SOMEWHERE, so a club that
  // simply closes at 11PM and never wanted an overnight session is not
  // nagged about a slot it has no use for.
  const overnightGap = useMemo(() => {
    if (timeline.length === 0) return null;

    // How many after-midnight hours the club is asking for. NOT simply "any
    // hour before 7AM" — 6-7AM is an ordinary opening hour, and counting it
    // would nag every club that closes at midnight about a slot it has no
    // use for. A day that genuinely runs past midnight shows it in the SHAPE
    // of its list: small hours sitting before a gap, then the morning block
    // (12-1AM, 1-2AM, [gap], 6-7AM …). A single unbroken run is a plain day.
    // Counts only the hours BEFORE that first gap — the wrap itself. Every
    // hour under 7AM would over-count: a list holding 12-1AM, 1-2AM and a
    // 6-7AM opening wants two overnight hours, not three, and treating the
    // opening hour as wanted leaves the message nagging a club that already
    // reaches exactly as far as it asked to.
    const overnightHourCount = (slots) => {
      const hours = sortTimeSlots(slots).map(parseTimeSlotStartHour);
      const firstGap = hours.findIndex((h, i) => i > 0 && h !== hours[i - 1] + 1);
      if (firstGap === -1) return 0;
      const head = hours.slice(0, firstGap);
      return head.every(h => h < OPEN_PLAY_NEXT_DAY_END_HOUR) ? head.length : 0;
    };

    // Either list can express the intent: the next day's, because that is
    // where the hours land, or the hosting day's, because "we run to 2am" is
    // how an admin describes the night they are hosting.
    const wanted = Math.max(overnightHourCount(nextDaySlots), overnightHourCount(sortedSlots));
    const reached = timeline.filter(e => e.dayOffset === 1).length;
    // Already as far as the club asked to go — nothing to report.
    if (wanted === 0 || reached >= wanted) return null;

    const nextAbs = timeline[timeline.length - 1].abs + 1;
    // Already as far as an Open Play session may run — nothing is missing.
    if (nextAbs >= 24 + OPEN_PLAY_NEXT_DAY_END_HOUR) return null;

    const dayOffset = nextAbs >= 24 ? 1 : 0;
    const forDate = dayOffset === 1 ? nextDateStr(dateKey(date)) : dateKey(date);
    const set = getTimeSlotSetForDate(config, forDate);
    return {
      slot: slotLabelForHour(nextAbs % 24),
      // Named so the admin knows WHICH list to edit — a club with a weekend
      // set will otherwise add the hour to the default one and see no change.
      setName: set ? (set.label?.trim() || describeSlotSetDays(set)) : 'Default hours',
      onNextDay: dayOffset === 1,
    };
  }, [sortedSlots, nextDaySlots, timeline, config, date]);

  // Boundaries between the timeline's hours, on the same anchored scale:
  // timeline[i] runs from slotBoundaries[i] to slotBoundaries[i + 1]. This is
  // what the Time start / Time end dropdowns offer and what turns a chosen
  // pair straight back into a slice of the timeline.
  const slotBoundaries = useMemo(() => {
    if (timeline.length === 0) return [];
    return [...timeline.map(e => e.abs), timeline[timeline.length - 1].abs + 1];
  }, [timeline]);

  // An absolute hour as the admin should read it — "2:00 AM (next day)".
  const formatAbsHour = (abs) => `${formatHourLabel(abs % 24)}${abs >= 24 ? ' (next day)' : ''}`;
  // Same, for a single hour SLOT rather than a boundary.
  const formatEntry = (entry) => (entry
    ? `${formatTimeSlotRange(entry.slot)}${entry.dayOffset === 1 ? ' (next day)' : ''}`
    : '');
  // Position of a (slot, dayOffset) pair in the timeline, or -1.
  const indexOfSlot = (slot, dayOffset = 0) =>
    timeline.findIndex(e => e.slot === slot && e.dayOffset === dayOffset);

  // The <input type="date"> is invisible (opacity: 0, stretched over the
  // whole pill) so the field can render "Aug 19, 2026" instead of the
  // browser's own dd/mm/yyyy widget. The cost is that clicking it does
  // NOTHING: browsers only open the calendar when its indicator icon is
  // clicked, and that icon is part of what opacity: 0 hides — leaving a
  // field that looks interactive and isn't. showPicker() opens it directly,
  // which is what makes the date changeable at all.
  const dateInputRef = useRef(null);
  // { [court]: number[] } — POSITIONS in `timeline`, not slot labels. A
  // position carries which day the hour belongs to; a bare "12-1AM" does
  // not, and an overnight session needs to tell tonight's 1AM from
  // tomorrow's. Converted back to labels (split across the two dates) at
  // save time, and from labels back to positions when an existing session is
  // opened for editing.
  const [courtSlots, setCourtSlots] = useState({});
  // { [court]: [{ slot, dayOffset }] } — hours this session already holds
  // that are NOT in Config -> Time Slots any more (the admin removed the slot
  // after the session was hosted). They have no position on the timeline, so
  // they can't be shown or edited here, but they ARE part of the session and
  // are written back untouched on save. Dropping them would delete an hour
  // the admin never asked to remove, and would leave its slotOverrides cell
  // orphaned with no session behind it.
  const [orphanSlots, setOrphanSlots] = useState({});
  // Which court's "set the time" popup is open (its court number), or null.
  const [activeCourtPicker, setActiveCourtPicker] = useState(null);
  // 'range' (Time start / Time end — the default) or 'manual' (the old
  // one-chip-at-a-time grid) — see openCourtPicker below for how a court
  // that already has hours picks its starting mode when reopened.
  const [pickerMode, setPickerMode] = useState('range');
  const [rangeStart, setRangeStart] = useState(null); // hour number or null
  const [rangeEnd, setRangeEnd] = useState(null); // hour number or null
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [fee, setFee] = useState(0);
  const [notes, setNotes] = useState('');
  // { 0: bookedSlotsForSelectedDate, 1: bookedSlotsForTheNextDate } — keyed
  // by the timeline's dayOffset so a lookup only needs the hour's position.
  const [bookedSlots, setBookedSlots] = useState({ 0: {}, 1: {} });
  // Every OTHER active Open Play session (this one excluded when editing),
  // so a court/hour already claimed by a different session can't be double-
  // booked into this one — see takenByOtherSession below.
  const [otherSessions, setOtherSessions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Everyone currently joined to the session being edited (self-service
  // joins plus admin-added walk-ins) — drives both the "X / max joined"
  // count and the player list below it. Always empty for a brand new
  // session, which has no id to attach joins to yet.
  const [joins, setJoins] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [playerError, setPlayerError] = useState('');
  // Walk-ins the admin has typed but not yet saved. Only ADDITIONS are
  // staged: a saved join can no longer be removed from this screen at all —
  // players leave a session by having their join request declined (Admin ->
  // Open Play, or the Reservation Queue), which is the same act that hands
  // their spots back to the cap and tells the guest what happened.
  const [stagedAdds, setStagedAdds] = useState([]);        // [{tempId, fullName, numPlayers}]
  // People the admin has marked to take off the session, not yet written —
  // { [joinId]: [rosterIndex, ...] }, keyed per PERSON rather than per
  // booking so one name can come out of a group of four without cancelling
  // the other three.
  //
  // Staged rather than immediate for the same reason adds are: this modal's
  // contract is that nothing lands until Save, so a mis-tap is undone by
  // tapping again (or by closing) instead of needing the guest's join
  // re-approved from another screen.
  const [stagedRemovals, setStagedRemovals] = useState({});
  const nextStagedId = useRef(0);

  // What the list and the counter show: every saved join, plus anything
  // staged to be added. Everything downstream reads this rather than
  // `joins`, so the modal shows the state the admin is about to save
  // instead of the one on the server.
  const visiblePlayers = useMemo(() => ([
    ...joins
      .map(j => ({
        key: j.id,
        id: j.id,
        name: j.fullName,
        numPlayers: j.numPlayers,
        // 'pending' = receipt attached, waiting on the admin. 'confirmed' =
        // approved. Drives the PENDING marker below.
        status: j.status,
        staged: false,
        // Everyone the join actually covers. A guest booking for their
        // group carries all their names; only the booker was shown, so
        // "Test 1 · 2" was one known person and one anonymous body — the
        // club couldn't check the second one in or even know who they were.
        //
        // Empty for an admin-added walk-in (addManualOpenPlayJoin takes a
        // name and a headcount, no roster) and for joins saved before
        // rosters existed — the count already says everything known there,
        // so those render exactly as they do now.
        names: (j.players || []).map(p => p.name).filter(Boolean),
      })),
    ...stagedAdds
      .map(p => ({ key: p.tempId, id: p.tempId, name: p.fullName, numPlayers: p.numPlayers, status: 'confirmed', staged: true, names: [] })),
  ]), [joins, stagedAdds]);

  // The list, flattened to ONE ROW PER PLAYER. Bookings used to render as a
  // booker row with the names it covers nested underneath, which made a
  // simple roster read as a tree — an admin checking people in wants a
  // single column of names, not a hierarchy.
  //
  // The booking is still the unit that gets removed, so its controls ride on
  // its first row (see `booking` below) rather than being repeated or lost.
  const playerRows = useMemo(() => visiblePlayers.flatMap((player) => {
    // Falls back to the booker when a join carries no roster — an
    // admin-added walk-in, or a join saved before rosters were stored.
    const names = player.names.length > 0 ? player.names : [player.name];
    // With a roster, a row IS one person and `index` is their position in
    // the stored `players` array, which is what removeOpenPlayPlayers takes.
    // Without one, the single row stands for the whole booking.
    const hasRoster = player.names.length > 0;
    const stagedIndexes = stagedRemovals[player.id] || [];
    return names.map((name, index) => ({
      // Names repeat within a group, so the key pairs the booking with the
      // position rather than trusting the name to be unique.
      key: `${player.key}-${index}`,
      name,
      staged: player.staged,
      // Marked on EVERY row of the booking, not just the first: a guest who
      // booked for four has four people waiting on the same approval, and
      // tagging only the booker would read as the other three being cleared.
      // A staged walk-in is never pending — it hasn't been written yet, so
      // there is nothing for the admin to approve.
      pending: !player.staged && player.status !== 'confirmed',
      // This person specifically, not their whole group.
      removing: stagedIndexes.includes(index),
      // Who gets an x. Confirmed and already saved only: a PENDING join is
      // still a request the admin has not answered, and the answer to that
      // is Confirm or Decline under Open Play — removing it here would skip
      // the review and leave a paid guest with no explanation. A staged
      // walk-in has its own discard x below.
      removable: !player.staged && player.status === 'confirmed',
      // Everything the x needs, on every row rather than only the first:
      // each row is now its own person to act on.
      joinId: player.id,
      playerIndex: index,
      // No roster behind this row, so its x takes the booking off whole.
      // Said plainly in the button's tooltip, so the admin isn't told they
      // are removing one person when the record can only lose all of them.
      wholeBooking: !hasRoster,
      groupSize: Number(player.numPlayers) || 1,
      booking: index === 0 ? player : null,
    }));
  }), [visiblePlayers, stagedRemovals]);

  // Excludes anyone staged for removal, so the counter, the "session is
  // full" guard on Add and the max-players floor all read the roster the
  // admin is about to save — removing two people has to make room for two.
  const joinedCount = visiblePlayers.reduce((sum, p) => {
    const total = Number(p.numPlayers) || 0;
    const staged = (stagedRemovals[p.id] || []).length;
    // A rosterless booking's one row stands for all of its spots, so staging
    // it frees the whole head count rather than a single place.
    const freed = p.names.length > 0 ? staged : (staged > 0 ? total : 0);
    return sum + Math.max(0, total - freed);
  }, 0);
  const hasStagedPlayerChanges = stagedAdds.length > 0 || Object.keys(stagedRemovals).length > 0;

  // Clean slate (or a pre-filled copy of `session`) every time the modal is
  // (re)opened.
  useEffect(() => {
    if (!isOpen) return;
    if (session) {
      setDate(parseDateStr(session.date));
      // Both halves of the session map back onto the timeline: `courts` are
      // day 0, `courtsNextDay` day 1. An hour whose slot has since been
      // removed from Config -> Time Slots resolves to -1 and is dropped
      // rather than poisoning the index math with it.
      const orphans = {};
      const toIndices = (map, dayOffset) => {
        const out = {};
        Object.entries(map || {}).forEach(([court, slots]) => {
          (slots || []).forEach((slot) => {
            const idx = indexOfSlot(slot, dayOffset);
            if (idx === -1) {
              // Not on the timeline any more — set aside, not discarded.
              if (!orphans[court]) orphans[court] = [];
              orphans[court].push({ slot, dayOffset });
              return;
            }
            if (!out[court]) out[court] = [];
            out[court].push(idx);
          });
        });
        return out;
      };
      const day0 = toIndices(session.courts, 0);
      const day1 = toIndices(session.courtsNextDay, 1);
      const merged = {};
      [...new Set([...Object.keys(day0), ...Object.keys(day1)])].forEach((court) => {
        merged[court] = [...(day0[court] || []), ...(day1[court] || [])].sort((a, b) => a - b);
      });
      setCourtSlots(merged);
      setOrphanSlots(orphans);
      setMaxPlayers(session.maxPlayers || 10);
      setFee(session.fee || 0);
      setNotes(session.notes || '');
    } else {
      setDate(clampDate(initialDate));
      setCourtSlots({});
      setOrphanSlots({});
      setMaxPlayers(10);
      setFee(0);
      setNotes('');
    }
    setActiveCourtPicker(null);
    setPickerMode('range');
    setRangeStart(null);
    setRangeEnd(null);
    setError('');
    setShowAddPlayer(false);
    setNewPlayerName('');
    setPlayerError('');
    // Reopening discards anything staged but never confirmed — that IS the
    // undo for a walk-in typed by mistake, and for a removal marked and then
    // thought better of.
    setStagedAdds([]);
    setStagedRemovals({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialDate, session]);

  // Live-updating booked-slot lookup for whichever date is picked, so a
  // "BOOKED" chip stays accurate even if a booking comes in while this
  // modal is open.
  // Two subscriptions, one per date the timeline can reach: an overnight
  // session's 2AM hour competes with TOMORROW's bookings, and checking it
  // against tonight's would let it be hosted straight over a real
  // reservation.
  useEffect(() => {
    if (!isOpen || !db) return undefined;
    const dateStr = dateKey(date);
    const endStr = nextDateStr(dateStr);
    const listen = (forDate, dayOffset) => {
      const q = query(ref(db, 'bookings'), orderByChild('date'), equalTo(forDate));
      return onValue(q, (snapshot) => {
        setBookedSlots(prev => ({ ...prev, [dayOffset]: buildBookedSlotsFromSnapshot(snapshot) }));
      }, (err) => {
        console.error('Error listening to bookings for Host Open Play:', err);
      });
    };
    const unsubs = [listen(dateStr, 0), listen(endStr, 1)];
    return () => unsubs.forEach(u => u());
  }, [isOpen, date]);

  // Live-updating list of every OTHER active Open Play session, so a
  // court/hour another session already owns shows as unavailable here (and
  // stays that way if a second admin hosts one while this modal is open).
  // The session currently being edited is filtered out — its own slots must
  // obviously stay selectable, otherwise editing one would immediately
  // block every hour it already holds.
  useEffect(() => {
    if (!isOpen) return undefined;
    const unsubscribe = subscribeOpenPlaySessions((all) => {
      setOtherSessions(all.filter(s => s.status === 'active' && s.id !== session?.id));
    });
    return () => unsubscribe();
  }, [isOpen, session?.id]);

  // Live player list for the session being edited, so the count and the
  // list below it stay correct if a guest joins while this modal is open.
  useEffect(() => {
    if (!isOpen || !session?.id) { setJoins([]); return undefined; }
    const unsubscribe = subscribeSessionJoins(session.id, setJoins);
    return () => unsubscribe();
  }, [isOpen, session?.id]);

  // Staged, not saved. Adding a walk-in used to write to Firebase the
  // instant "Add" was pressed and removing one deleted immediately — so a
  // mistyped name was live to every guest before the admin could react, and
  // Cancel/× undid nothing. Both now sit here until Confirm, which is what
  // the button already promised for every other field in this modal.
  const handleAddPlayer = () => {
    setPlayerError('');
    const name = newPlayerName.trim();
    if (!name) {
      setPlayerError('Enter the player\'s name.');
      return;
    }
    // Counts against the STAGED total, so several adds in a row can't each
    // pass a check that only the first would really have room for.
    const remaining = (Number(maxPlayers) || 0) - joinedCount;
    if (remaining < 1) {
      setPlayerError('This session is full.');
      return;
    }
    setStagedAdds(prev => [...prev, {
      // Local key only — a real Firebase id doesn't exist until Confirm.
      tempId: `staged-${nextStagedId.current++}`,
      fullName: name,
      // Always one. A headcount field let an admin add "Group of 6" as a
      // single entry, which is the one thing the roster can't show: six
      // people arrive and five of them have no name anywhere. Adding them
      // one at a time is what makes the list checkable at the door, and
      // matches how a guest books their own group.
      numPlayers: 1,
    }]);
    setNewPlayerName('');
    // Stays open so the next name can be typed straight away — adding six
    // players shouldn't mean pressing "+ Add player" six times.
    setPlayerError('');
  };

  /**
   * Discards a walk-in the admin typed but hasn't saved.
   *
   * Only ever applies to a staged row — nothing was written for it, so this
   * is undoing an edit rather than removing anyone from the session. Saved
   * joins have no remove control at all: a player leaves by having their
   * join request declined, which is what returns their spots to the cap and
   * gives the guest a status they can see.
   *
   * @param {string} tempId - the staged row's local id
   */
  const handleDiscardStagedPlayer = (tempId) => {
    setPlayerError('');
    setStagedAdds(prev => prev.filter(p => p.tempId !== tempId));
  };

  /**
   * Marks ONE person for removal, or takes the mark back off.
   *
   * A toggle rather than a one-way action so the x doubles as its own undo:
   * nothing is written until Save, and the row stays on screen struck
   * through in the meantime.
   *
   * @param {string} joinId - the saved join's Firebase id
   * @param {number} playerIndex - their position in that join's roster
   * @returns {void}
   */
  const handleToggleRemovePlayer = (joinId, playerIndex) => {
    setPlayerError('');
    setStagedRemovals((prev) => {
      const current = prev[joinId] || [];
      const next = current.includes(playerIndex)
        ? current.filter(i => i !== playerIndex)
        : [...current, playerIndex];
      // Drop the key entirely once its last mark is undone, so
      // hasStagedPlayerChanges can just count keys.
      if (next.length === 0) {
        const { [joinId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [joinId]: next };
    });
  };

  /**
   * Writes the staged walk-ins. Called by Confirm, never before.
   *
   * A brand new session has no id until saveOpenPlaySession returns, so the
   * id is passed in rather than read from the `session` prop.
   * @param {string} sessionId
   */
  const applyStagedPlayerChanges = async (sessionId) => {
    if (!sessionId) return;
    // Removals first: they hand spots back, so an admin who swapped one
    // player for another on a full session doesn't hit the cap on the add.
    //
    // One call per JOIN, carrying every index marked on it, rather than one
    // call per person: each removal rewrites that join's roster, so applying
    // them one at a time would have the second call working from indexes
    // that the first had already shifted.
    for (const [joinId, indexes] of Object.entries(stagedRemovals)) {
      await removeOpenPlayPlayers(joinId, indexes);
    }
    setStagedRemovals({});
    for (const player of stagedAdds) {
      await addManualOpenPlayJoin({
        sessionId,
        sportId: effectiveSportId,
        sportName: sport?.name || '',
        date: dateKey(date),
        fullName: player.fullName,
        numPlayers: player.numPlayers,
        pricePerPlayer: Number(fee) || 0,
      });
    }
    setStagedAdds([]);
  };

  // { [court]: { [slot]: sessionLabel } } for the currently-picked date —
  // the label is only used for the "already used by ..." error text.
  // { [dayOffset]: { [court]: { [slot]: sessionLabel } } } across BOTH dates
  // the timeline covers. getSessionSlotsForDate does the hard part: it hands
  // back only the half of an overnight session that falls on the date asked
  // for, so the other session's 11PM and its 2AM block the right hours.
  const otherSessionSlots = useMemo(() => {
    const byDay = {};
    [0, 1].forEach((dayOffset) => {
      const forDate = dayOffset === 0 ? dateKey(date) : nextDateStr(dateKey(date));
      const map = {};
      otherSessions.forEach((s) => {
        Object.entries(getSessionSlotsForDate(s, forDate)).forEach(([court, slots]) => {
          (slots || []).forEach((slot) => {
            if (!map[court]) map[court] = {};
            map[court][slot] = s.sportName ? `${s.sportName} Open Play` : 'another Open Play session';
          });
        });
      });
      byDay[dayOffset] = map;
    });
    return byDay;
  }, [otherSessions, date]);

  // The (court, slot) pairs this session ALREADY owned when the modal was
  // opened. They are exempt from the "hour has passed" rule below: an admin
  // editing a session that is currently running — or one that finished an
  // hour ago — must not be blocked by the very hours it was created with,
  // or its player list becomes uneditable exactly when it is being used.
  const originalSlots = useMemo(() => {
    const map = {};
    const add = (source, dayOffset) => {
      Object.entries(source || {}).forEach(([court, slots]) => {
        const key = String(court);
        if (!map[key]) map[key] = new Set();
        (slots || []).forEach(slot => map[key].add(`${dayOffset}|${slot}`));
      });
    };
    add(session?.courts, 0);
    add(session?.courtsNextDay, 1);
    return map;
  }, [session]);
  const wasAlreadyInSession = (court, entry) =>
    !!entry && !!originalSlots[String(court)]?.has(`${entry.dayOffset}|${entry.slot}`);

  const selectedDateKey = dateKey(date);
  const isViewingToday = selectedDateKey === todayKey;
  // Same expression the admin grid uses for its own "passed" cells, so the
  // grid and this modal can never disagree about which hours are still
  // hostable today (AdminTimeSlotSelection.jsx).
  const currentHour = new Date().getHours();
  // An hour that has already started today. Hosting into it is the same
  // mistake as hosting on a past date, just at a finer grain — nobody can
  // join a rotation that began two hours ago.
  //
  // Every check below takes a POSITION in the timeline rather than a slot
  // label, because the same label means two different hours on the two sides
  // of midnight: "12-1AM" tonight is the hour just gone, "12-1AM" tomorrow
  // is five hours away. The position resolves which, and with it which
  // date's bookings and sessions to check against.
  const entryAt = (idx) => timeline[idx] || null;

  const isPastHour = (court, idx) => {
    const entry = entryAt(idx);
    if (!entry) return false;
    // Only TONIGHT's hours can have passed. A next-day hour is by definition
    // still ahead, however late it is now.
    if (entry.dayOffset === 1) return false;
    return isSlotPassed(isViewingToday, entry.slot, currentHour) && !wasAlreadyInSession(court, entry);
  };

  const isBooked = (court, idx) => {
    const entry = entryAt(idx);
    if (!entry) return false;
    return ((bookedSlots[entry.dayOffset] || {})[entry.slot] || []).some(b => b.court === court);
  };

  // A court/hour already claimed by a DIFFERENT Open Play session. Court
  // keys come back from Firebase as strings, so this checks both forms
  // rather than relying on the caller passing one or the other.
  const takenByOtherSession = (court, idx) => {
    const entry = entryAt(idx);
    if (!entry) return null;
    const forDay = otherSessionSlots[entry.dayOffset] || {};
    return forDay[court]?.[entry.slot] || forDay[String(court)]?.[entry.slot] || null;
  };

  // Anything this session can't claim, for whatever reason.
  const isUnavailable = (court, idx) =>
    isBooked(court, idx) || !!takenByOtherSession(court, idx) || isPastHour(court, idx);

  const toggleSlot = (court, idx) => {
    if (isUnavailable(court, idx)) return;
    setCourtSlots(prev => {
      const current = prev[court] || [];
      const next = current.includes(idx)
        ? current.filter(i => i !== idx)
        : [...current, idx].sort((a, b) => a - b);
      return { ...prev, [court]: next };
    });
  };

  /**
   * A court's chosen hours as start–end range(s) rather than every
   * individual slot — "4:00 PM – 12:00 MN" instead of eight comma-separated
   * hour labels, which wrapped to four lines per court and made the list
   * unreadable. Consecutive slots collapse into one range; a gap (only
   * reachable via the manual picker) starts a new one, so a split schedule
   * still reads honestly as "9:00 AM – 11:00 AM, 5:00 PM – 8:00 PM".
   */
  const summaryFor = (court) => {
    const indices = [...(courtSlots[court] || [])].sort((a, b) => a - b);
    if (indices.length === 0) return 'Tap to set time';

    const ranges = [];
    let runStart = indices[0];
    let prev = indices[0];
    // formatAbsHour, so a run ending after midnight says so rather than
    // reading as an hour this morning.
    const flush = () => {
      ranges.push(`${formatAbsHour(slotBoundaries[runStart])} – ${formatAbsHour(slotBoundaries[prev + 1])}`);
    };
    indices.slice(1).forEach(i => {
      if (i === prev + 1) { prev = i; return; }
      flush();
      runStart = i;
      prev = i;
    });
    flush();
    return ranges.join(', ');
  };

  // If a court's already-picked slots happen to form one unbroken range
  // (the common case — most Open Play sessions just run start-to-end), the
  // popup reopens in range mode pre-filled to match it. Slots with a gap in
  // them (only possible via the manual picker) instead reopen in manual
  // mode showing exactly what's selected, rather than silently collapsing
  // them into a range that doesn't represent the real selection.
  const findRangeForSlots = (indices) => {
    if (!indices || indices.length === 0) return null;
    const sorted = [...indices].sort((a, b) => a - b);
    // One unbroken run: the positions must be consecutive with no gap.
    const unbroken = sorted.every((idx, i) => i === 0 || idx === sorted[i - 1] + 1);
    if (!unbroken) return null;
    return { start: slotBoundaries[sorted[0]], end: slotBoundaries[sorted[sorted.length - 1] + 1] };
  };

  const openCourtPicker = (court) => {
    setActiveCourtPicker(court);
    const existing = courtSlots[court] || [];
    const range = findRangeForSlots(existing);
    if (range) {
      setPickerMode('range');
      setRangeStart(range.start);
      setRangeEnd(range.end);
    } else {
      setPickerMode(existing.length > 0 ? 'manual' : 'range');
      setRangeStart(null);
      setRangeEnd(null);
    }
  };

  // The slots a chosen Time start / Time end pair actually covers —
  // timeline[i] runs from slotBoundaries[i] to slotBoundaries[i+1], so once
  // both boundaries are known this is just a slice between their positions
  // in that shared sequence — which is precisely why a range crossing
  // midnight needs no special case here.
  const rangeStartIdx = rangeStart === null ? -1 : slotBoundaries.indexOf(rangeStart);
  const rangeEndIdx = rangeEnd === null ? -1 : slotBoundaries.indexOf(rangeEnd);
  // Positions, not labels — the same currency courtSlots is kept in.
  const rangeCoveredSlots = (rangeStartIdx === -1 || rangeEndIdx === -1 || rangeEndIdx <= rangeStartIdx)
    ? []
    : Array.from({ length: rangeEndIdx - rangeStartIdx }, (_, i) => rangeStartIdx + i);
  // Slots inside the chosen range this court can't actually take, split by
  // reason so the message below can say which it is (a real booking vs.
  // another Open Play session already holding that hour).
  const rangeBookedConflicts = activeCourtPicker === null
    ? []
    : rangeCoveredSlots.filter(idx => isBooked(activeCourtPicker, idx));
  const rangeSessionConflicts = activeCourtPicker === null
    ? []
    : rangeCoveredSlots.filter(idx => !isBooked(activeCourtPicker, idx) && takenByOtherSession(activeCourtPicker, idx));
  // Hours in the chosen range that are already behind us today.
  const rangePastConflicts = activeCourtPicker === null
    ? []
    : rangeCoveredSlots.filter(idx =>
        !isBooked(activeCourtPicker, idx)
        && !takenByOtherSession(activeCourtPicker, idx)
        && isPastHour(activeCourtPicker, idx));
  const rangeConflictSlots = [...rangeBookedConflicts, ...rangeSessionConflicts, ...rangePastConflicts];

  // ---- Which hours the Time start / Time end dropdowns may offer --------
  //
  // Both dropdowns used to list every configured hour unconditionally, so
  // the only way to discover that a court was busy was to pick both ends and
  // read the error underneath. The options now carry that answer themselves,
  // which also means a clash can't be selected in the first place.
  //
  // The reason is worth keeping distinct from the boolean: "unavailable"
  // alone reads as a bug when the admin can plainly see the court is free
  // right now — it's usually another Open Play session holding the hour.
  const slotBlockedReason = (court, idx) => {
    if (court === null) return null;
    if (isBooked(court, idx)) return 'already booked';
    if (takenByOtherSession(court, idx)) return `already part of ${takenByOtherSession(court, idx)}`;
    if (isPastHour(court, idx)) return 'already passed today';
    return null;
  };

  // timeline[i] runs from slotBoundaries[i] to slotBoundaries[i + 1], so a
  // start hour is unusable exactly when the first hour it would claim is.
  const startOptionReason = (h) => {
    const idx = slotBoundaries.indexOf(h);
    if (idx === -1 || idx >= timeline.length) return null;
    return slotBlockedReason(activeCourtPicker, idx);
  };

  // An end hour is unusable when ANY hour between the chosen start and it is
  // blocked — a range is contiguous, so it can't hop over a booked hour.
  // Everything past the first blocked slot is therefore out too, not just
  // the blocked hour itself, which is what stops the admin from picking an
  // end that looks fine and fails validation a step later.
  const endOptionReason = (h) => {
    if (rangeStartIdx === -1) return null;
    const idx = slotBoundaries.indexOf(h);
    if (idx === -1) return null;
    for (let i = rangeStartIdx; i < idx && i < timeline.length; i += 1) {
      const reason = slotBlockedReason(activeCourtPicker, i);
      if (reason) return reason;
    }
    return null;
  };

  // Nothing at all left on this court for this date — worth saying outright
  // rather than presenting a dropdown in which every row is struck out.
  const allStartsBlocked = activeCourtPicker !== null
    && timeline.length > 0
    && slotBoundaries.slice(0, -1).every(h => !!startOptionReason(h));

  const applyRangeToCourt = () => {
    if (activeCourtPicker === null || rangeCoveredSlots.length === 0 || rangeConflictSlots.length > 0) return;
    setCourtSlots(prev => ({ ...prev, [activeCourtPicker]: rangeCoveredSlots }));
    setActiveCourtPicker(null);
  };

  if (!isOpen) return null;

  // Deliberately NOT closed by a backdrop click (CLAUDE.md > Behavior).
  // The form holds staged court hours and staged player edits that are
  // discarded on close, so an accidental tap outside silently threw away
  // work. The × in the header is the way out.

  /**
   * Opens the native calendar for the hidden date input.
   *
   * showPicker() is the only way to do this deliberately — every other
   * approach (focus(), a synthetic click()) is at the browser's discretion
   * and silently does nothing in most of them.
   *
   * It THROWS rather than no-ops when the picker is already showing, which
   * happens when the click landed on the input's own (invisible) indicator
   * and the browser opened it before this handler ran. That case is already
   * the desired outcome, so it's swallowed — deliberately without falling
   * back to focus(), which would just close the calendar again.
   *
   * Where showPicker doesn't exist at all (Safari < 16), focus() at least
   * puts the caret in the field so the date can be typed — the behaviour
   * this field had everywhere before.
   */
  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    if (typeof input.showPicker !== 'function') { input.focus(); return; }
    try { input.showPicker(); } catch { /* already open, or blocked — leave it */ }
  };

  const handleConfirm = async () => {
    setError('');
    const entries = Object.entries(courtSlots).filter(([, indices]) => indices.length > 0);
    // A court holding ONLY hours that have left the config still counts as
    // part of the session — otherwise editing just the fee on such a session
    // would be refused as "no courts picked".
    const orphanEntries = Object.entries(orphanSlots).filter(([, list]) => (list || []).length > 0);
    if (entries.length === 0 && orphanEntries.length === 0) {
      setError('Tap at least one court and pick its time slots.');
      return;
    }
    // Now that the cap can be typed, it can be typed wrong. The dropdown
    // could only ever produce a valid whole number; a text field can produce
    // "", "0", "3.5" or "-2", none of which the rest of the app is prepared
    // for — a fractional cap makes "12 / 10.5 joined" and a zero cap makes a
    // session nobody can ever join.
    const capValue = Number(maxPlayers);
    if (!maxPlayers || !Number.isInteger(capValue) || capValue < 1) {
      setError('Set a max number of players — a whole number, 1 or more.');
      return;
    }
    // Also reachable only by typing: dropping the cap below the players who
    // are already in would leave the session permanently over capacity, with
    // a counter reading "14 / 10" and no way for it to settle.
    if (capValue < joinedCount) {
      setError(
        `This session already has ${joinedCount} player${joinedCount === 1 ? '' : 's'}. ` +
        `The cap can't be lower than that — remove players first, or set it to ${joinedCount} or more.`
      );
      return;
    }

    // The date is re-checked here rather than trusted from the picker. Two
    // ways a past date reaches this point despite `min` and the onChange
    // guard: the modal was opened before midnight and confirmed after it, or
    // the input was driven by something other than a click. Both write a
    // session nobody can ever join, so both stop here.
    if (selectedDateKey < minDateKey) {
      setError(
        minDateKey === todayKey
          ? 'Open Play can only be hosted for today or a later date.'
          : `This session can't be moved earlier than ${formatDateLabel(parseDateStr(minDateKey))}.`
      );
      return;
    }

    // Re-check every picked slot at save time, not just when it was picked.
    // The per-court pickers validate against whichever date was selected at
    // that moment — changing the date afterwards (or another admin hosting
    // a session in the meantime) can invalidate an earlier selection, and
    // this is the last point where that's catchable before writing.
    for (const [court, indices] of entries) {
      for (const idx of indices) {
        const entry = entryAt(idx);
        if (!entry) continue;
        const when = formatEntry(entry);
        if (isBooked(court, idx)) {
          setError(`${getCourtLabel(config, court)} at ${when} is already booked. Adjust that court's time.`);
          return;
        }
        const otherLabel = takenByOtherSession(court, idx);
        if (otherLabel) {
          setError(`${getCourtLabel(config, court)} at ${when} is already part of ${otherLabel}. Adjust that court's time.`);
          return;
        }
        // Picked while the hour was still ahead, confirmed after it started.
        // Slots the session already held are exempt (see isPastHour).
        if (isPastHour(court, idx)) {
          setError(`${when} has already passed today. Adjust ${getCourtLabel(config, court)}'s time.`);
          return;
        }
      }
    }

    // Positions back to slot labels, split by which date they land on. This
    // is the only place the timeline is unwound, and it is what turns one
    // continuous 10PM-6AM selection into the two dates the session record
    // and the slotOverrides grid are actually keyed by.
    const day0 = {};
    const day1 = {};
    entries.forEach(([court, indices]) => {
      [...indices].sort((a, b) => a - b).forEach((idx) => {
        const entry = entryAt(idx);
        if (!entry) return;
        const target = entry.dayOffset === 0 ? day0 : day1;
        if (!target[court]) target[court] = [];
        target[court].push(entry.slot);
      });
    });
    // Hours the config no longer lists go back exactly as they came in. They
    // were never shown, so the admin never chose to remove them, and a save
    // that quietly dropped them would delete part of a live session.
    orphanEntries.forEach(([court, list]) => {
      list.forEach(({ slot, dayOffset }) => {
        const target = dayOffset === 0 ? day0 : day1;
        if (!target[court]) target[court] = [];
        if (!target[court].includes(slot)) target[court].push(slot);
      });
    });

    setSubmitting(true);
    try {
      const savedSessionId = await saveOpenPlaySession(session, {
        sportId: effectiveSportId,
        sportName: sport?.name || '',
        dateStr: dateKey(date),
        courtSlots: day0,
        courtSlotsNextDay: day1,
        maxPlayers,
        fee,
        notes,
      });
      // Player edits are written only now, after the session itself saved.
      // Ordered this way on purpose: if the session write fails, the roster
      // is left exactly as it was rather than half-updated against a
      // session that never changed.
      await applyStagedPlayerChanges(savedSessionId);
      onHosted?.(session ? 'edit' : 'create');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save this Open Play session. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="ahop-overlay">
      <div className="ahop-card">
        <div className="ahop-head">
          <h2 className="ahop-title">{session ? 'Edit Open Play session' : 'Host an Open Play session'}</h2>
          <button type="button" className="ahop-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="ahop-body">
          <div className="ahop-field">
            <label className="ahop-label">Select date</label>
            {/* The whole pill is the control, not just the invisible input
                inside it — clicking anywhere on it opens the calendar, and
                it's reachable by keyboard like a button. */}
            <div
              className="ahop-date-wrap"
              role="button"
              tabIndex={0}
              onClick={openDatePicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDatePicker(); }
              }}
            >
              <Calendar size={16} className="ahop-date-icon" />
              <span className="ahop-date-text">{formatDateLabel(date)}</span>
              <span className="ahop-date-change">Change</span>
              <input
                ref={dateInputRef}
                type="date"
                className="ahop-date-input"
                // The wrapper above is the tab stop. Without this the
                // invisible input is a second one, so Tab lands on a control
                // nobody can see.
                tabIndex={-1}
                // Greys out every past day in the native picker. Presentation
                // only — a typed or pasted value still fires onChange, and
                // Firefox ignores `min` on manual entry entirely, so the
                // check below is the one that actually holds.
                min={minDateKey}
                value={selectedDateKey}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value || value < minDateKey) return;
                  const [y, m, d] = value.split('-').map(Number);
                  setError('');
                  setDate(new Date(y, m - 1, d));
                }}
                aria-label="Select date"
              />
            </div>
            {minDateKey < todayKey && (
              <p className="ahop-hint">
                This session was hosted for a past date. You can still edit it, but it can&rsquo;t be moved
                further back.
              </p>
            )}
          </div>

          <div className="ahop-field">
            <label className="ahop-label">Select courts and times</label>
            {courts.length === 0 ? (
              <p className="ahop-empty">No courts are configured for {sportName} yet — add one in Config &rarr; Sports &amp; Courts.</p>
            ) : (
              <div className="ahop-courts">
                {courts.map(court => (
                  <div className="ahop-court-block" key={court}>
                    <button
                      type="button"
                      className="ahop-court-row"
                      onClick={() => openCourtPicker(court)}
                    >
                      <span className="ahop-court-name">{getCourtLabel(config, court)}</span>
                      <span className={`ahop-court-summary${(courtSlots[court] || []).length > 0 ? ' filled' : ''}`}>
                        {summaryFor(court)}
                      </span>
                    </button>
                    {/* Kept, but not editable from here — the hour is no
                        longer in Config -> Time Slots, so there is no chip or
                        dropdown row to show it on. Said out loud so the
                        court's summary line above isn't quietly wrong about
                        how long this court is booked for. */}
                    {(orphanSlots[court] || []).length > 0 && (
                      <p className="ahop-orphan-note">
                        Plus {orphanSlots[court].length} hour{orphanSlots[court].length === 1 ? '' : 's'}
                        {' '}({orphanSlots[court].map(o => formatTimeSlotRange(o.slot)).join(', ')}) no longer in
                        Config &rarr; Time Slots. Kept as is; add the slot back there to edit it.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="ahop-hint">
              Open Play runs on the {sportName.toLowerCase()} courts only. Tap a court to set its own start and end
              time — courts you leave blank are not part of the session.
            </p>
          </div>

          <div className="ahop-field">
            <label className="ahop-label">Currently joining</label>
            <div className="ahop-joining-row">
              <div className="ahop-joining-box">
                <span className="ahop-joining-count">{joinedCount}</span>
                <span className="ahop-joining-of">/ {maxPlayers || 0} joined</span>
              </div>
              {/* Now available on a brand new session too: staged players
                  are written after Confirm, once the session has an id, so
                  there's no longer anything to "save first". */}
              <button
                type="button"
                className="ahop-add-player-btn"
                onClick={() => { setShowAddPlayer(v => !v); setPlayerError(''); }}
              >
                {showAddPlayer ? 'Cancel' : '+ Add player'}
              </button>
            </div>

            {showAddPlayer && (
              <div className="ahop-add-player-form">
                {/* One name per Add — no headcount field. See handleAddPlayer. */}
                <input
                  type="text"
                  className="ahop-player-name-input"
                  placeholder="Player name"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPlayer(); } }}
                  autoFocus
                />
                {/* No longer async, and no longer has a loading state:
                    adding a player is a local edit until Confirm. */}
                <button type="button" className="ahop-player-save-btn" onClick={handleAddPlayer}>
                  Add
                </button>
              </div>
            )}

            {playerError && <p className="ahop-error">{playerError}</p>}

            {playerRows.length > 0 && (
              <ul className="ahop-player-list">
                {playerRows.map(row => (
                  <li key={row.key} className={`ahop-player-row${row.staged ? ' staged' : ''}${row.removing ? ' removing' : ''}`}>
                    <span className="ahop-player-name" title={row.name}>{row.name}</span>

                    {/* A row is a name and, at most, one tag and one button.
                        No headcount: the count only ever described the
                        booking behind the row, which is not what an admin
                        reading a roster is looking for.

                        Three cases, and a row is only ever in one of them:
                        PENDING (paid, not yet reviewed — no button, the
                        answer to a request is Confirm/Decline under Open
                        Play), confirmed-and-saved (the × that takes THIS
                        person off, leaving the rest of their group on), and
                        unsaved walk-in (NEW plus a discard ×, which is
                        undoing a line just typed rather than removing
                        anyone). The discard × still rides on the booking's
                        first row — a staged add is one line, not a roster. */}
                    {/* Paid, waiting on this admin. Sits on the row itself
                        rather than beside a headcount, so it survives the
                        one-name-per-row layout. */}
                    {row.pending && (
                      <span
                        className="ahop-player-tag ahop-player-tag-pending"
                        title="Payment received — waiting for your confirmation"
                      >PENDING</span>
                    )}

                    {/* Marked to come off the session on Save. Per person:
                        one name out of a group of four gets the tag, the
                        other three stay untouched. */}
                    {row.removing && (
                      <span className="ahop-player-tag ahop-player-tag-removing">REMOVING</span>
                    )}

                    {row.removable && (
                      <button
                        type="button"
                        className="ahop-player-remove"
                        onClick={() => handleToggleRemovePlayer(row.joinId, row.playerIndex)}
                        aria-label={row.removing
                          ? `Keep ${row.name} on this session`
                          : `Remove ${row.name} from this session`}
                        title={row.removing
                          ? 'Undo — keep this player on the session'
                          : (row.wholeBooking && row.groupSize > 1
                            ? `Take this booking (${row.groupSize} players) off the session`
                            : 'Take this player off the session')}
                      >{row.removing ? '\u21ba' : '\u00d7'}</button>
                    )}

                    {row.staged && row.booking && (
                      <>
                        <span className="ahop-player-tag">NEW</span>
                        <button
                          type="button"
                          className="ahop-player-remove"
                          onClick={() => handleDiscardStagedPlayer(row.booking.id)}
                          aria-label={`Discard ${row.name}, not yet saved`}
                          title="Discard this unsaved walk-in"
                        >&times;</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {hasStagedPlayerChanges && (
              <p className="ahop-hint ahop-hint-staged">
                Player changes are not saved yet — press {session ? 'Save changes' : 'Confirm'} to apply them.
              </p>
            )}

            {/* Says where removal actually lives, so the missing × reads as
                a deliberate rule rather than a gap. */}
            {playerRows.length > 0 && (
              <p className="ahop-hint">
                The &times; takes that one player off this session and reopens their slot — the rest of their group
                stays. A PENDING player has not been reviewed yet; confirm or decline their join request under
                Open Play instead.
              </p>
            )}
          </div>

          <div className="ahop-field">
            <label className="ahop-label" htmlFor="ahop-max-players">Max # of players</label>
            <NumberComboField
              id="ahop-max-players"
              value={maxPlayers}
              options={MAX_PLAYERS_OPTIONS}
              onChange={(next) => { setError(''); setMaxPlayers(next); }}
              ariaLabel="Max number of players"
              optionsLabel="Common player caps"
            />
            <p className="ahop-hint">Type any number, or pick a common one from the list.</p>
          </div>

          <div className="ahop-field">
            <label className="ahop-label">Open Play fee</label>
            <div className="ahop-fee-wrap">
              <span className="ahop-peso">{getCurrencySymbol(config)}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="ahop-fee-input"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
              <span className="ahop-cents">.00</span>
            </div>
          </div>

          <div className="ahop-field">
            <label className="ahop-label">Session notes / RSVP link</label>
            <textarea
              className="ahop-textarea"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={`${config?.courtName || 'Club'} ${sport?.name || ''} Open Play\n${config?.locationText || 'Court location'}\nRSVP: (optional link)`}
            />
            <p className="ahop-hint">Optional. Shown to every player after they join, with any link made clickable.</p>
          </div>

          {error && <p className="ahop-error">{error}</p>}

          <button type="button" className="ahop-confirm" onClick={handleConfirm} disabled={submitting}>
            {submitting ? (session ? 'Saving…' : 'Hosting…') : (session ? 'Save changes' : 'Confirm')}
          </button>
        </div>
      </div>

      {/* "Court N — set the time" popup, on top of the main card above.
          Defaults to the Time start / Time end range picker; "Or select
          specific time slots manually" swaps it to the old one-chip-at-a-
          time grid for a court that needs a non-contiguous schedule. */}
      {activeCourtPicker !== null && (
        <div
          className="ahop-nested-overlay"
          // Same rule as the modal behind it: the × closes this, a stray
          // tap outside does not discard the range being picked.
        >
          <div className="ahop-nested-card">
            <div className="ahop-nested-head">
              <h3 className="ahop-nested-title">{getCourtLabel(config, activeCourtPicker)} — set the time</h3>
              <button
                type="button"
                className="ahop-close"
                onClick={() => setActiveCourtPicker(null)}
                aria-label="Close"
              >&times;</button>
            </div>

            {pickerMode === 'range' ? (
              <>
                <div className="ahop-range-row">
                  <div className="ahop-range-field">
                    <label className="ahop-range-label">Time start</label>
                    <select
                      className="ahop-select"
                      value={rangeStart ?? ''}
                      onChange={(e) => {
                        setRangeStart(e.target.value === '' ? null : Number(e.target.value));
                        setRangeEnd(null);
                      }}
                    >
                      <option value="">Select</option>
                      {slotBoundaries.slice(0, -1).map(h => {
                        const reason = startOptionReason(h);
                        return (
                          <option
                            key={h}
                            value={h}
                            disabled={!!reason}
                            title={reason ? `This hour is ${reason}` : undefined}
                          >
                            {formatAbsHour(h)}{reason ? ' — Unavailable' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="ahop-range-field">
                    <label className="ahop-range-label">Time end</label>
                    <select
                      className="ahop-select"
                      value={rangeEnd ?? ''}
                      disabled={rangeStart === null}
                      onChange={(e) => setRangeEnd(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">&mdash;</option>
                      {rangeStart !== null && slotBoundaries.slice(slotBoundaries.indexOf(rangeStart) + 1).map(h => {
                        const reason = endOptionReason(h);
                        return (
                          <option
                            key={h}
                            value={h}
                            disabled={!!reason}
                            title={reason ? `An hour in this range is ${reason}` : undefined}
                          >
                            {formatAbsHour(h)}{reason ? ' — Unavailable' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Why the hours stop where they do. Sits where the admin is
                    looking for the hour that isn't there. */}
                {overnightGap && (
                  <p className="ahop-hint">
                    This picker stops here because {overnightGap.onNextDay ? 'the next day has' : 'this date has'} no{' '}
                    <strong>{overnightGap.slot}</strong> slot. Add it under Config &rarr; Sports &amp; Courts
                    &rarr; Time Slots, in <strong>{overnightGap.setName}</strong>, to carry the session
                    further into the night.
                  </p>
                )}
                {allStartsBlocked ? (
                  <p className="ahop-range-error">
                    {getCourtLabel(config, activeCourtPicker)} has no free hours left on this date. Pick another court,
                    or host this session on a different date.
                  </p>
                ) : rangeStart === null ? (
                  <p className="ahop-hint">
                    Choose a time start first. Hours marked Unavailable are already taken.
                    {timeline.some(e => e.dayOffset === 1)
                      ? ' Hours marked (next day) run past midnight into the following morning.'
                      : ''}
                  </p>
                ) : rangeEnd === null ? (
                  <p className="ahop-hint">Choose a time end.</p>
                ) : rangeBookedConflicts.length > 0 ? (
                  <p className="ahop-range-error">
                    {getCourtLabel(config, activeCourtPicker)} already has a booking at {formatEntry(entryAt(rangeBookedConflicts[0]))} within
                    this range. Choose a different time range.
                  </p>
                ) : rangeSessionConflicts.length > 0 ? (
                  <p className="ahop-range-error">
                    {getCourtLabel(config, activeCourtPicker)} at {formatEntry(entryAt(rangeSessionConflicts[0]))} is already part of{' '}
                    {takenByOtherSession(activeCourtPicker, rangeSessionConflicts[0])}. Choose a different time range.
                  </p>
                ) : rangePastConflicts.length > 0 ? (
                  <p className="ahop-range-error">
                    {formatEntry(entryAt(rangePastConflicts[0]))} has already passed today. Choose a later time range,
                    or host this session on another date.
                  </p>
                ) : (
                  <p className="ahop-hint">{rangeCoveredSlots.length} hour{rangeCoveredSlots.length === 1 ? '' : 's'} selected.</p>
                )}

                <button
                  type="button"
                  className="ahop-confirm ahop-nested-confirm"
                  disabled={rangeStart === null || rangeEnd === null || rangeConflictSlots.length > 0}
                  onClick={applyRangeToCourt}
                >
                  Check court
                </button>

                <button type="button" className="ahop-manual-link" onClick={() => setPickerMode('manual')}>
                  Or select specific time slots manually
                </button>
              </>
            ) : (
              <>
                <div className="ahop-slot-grid ahop-nested-slot-grid">
                  {/* The whole timeline, so the chips run straight past
                      midnight into the next morning's hours — those carry a
                      NEXT DAY tag rather than sitting at the top of the list
                      looking like hours that already went by. */}
                  {timeline.map((entry, idx) => {
                    const booked = isBooked(activeCourtPicker, idx);
                    // Claimed by a different Open Play session — unavailable
                    // for the same reason a booking is, just labelled
                    // differently so the admin knows why.
                    const otherSessionLabel = booked ? null : takenByOtherSession(activeCourtPicker, idx);
                    // An hour that has already started today — shown greyed
                    // out with a reason rather than hidden, so the picker
                    // still reads as the full day and it's obvious why the
                    // early hours can't be picked.
                    const passed = !booked && !otherSessionLabel && isPastHour(activeCourtPicker, idx);
                    const unavailable = booked || !!otherSessionLabel || passed;
                    const selected = (courtSlots[activeCourtPicker] || []).includes(idx);
                    return (
                      <button
                        key={`${entry.dayOffset}-${entry.slot}`}
                        type="button"
                        className={`ahop-slot-chip${selected ? ' selected' : ''}${unavailable ? ' booked' : ''}`}
                        disabled={unavailable}
                        title={
                          otherSessionLabel ? `Already part of ${otherSessionLabel}`
                            : passed ? 'This hour has already passed today'
                              : entry.dayOffset === 1 ? 'The morning after the selected date'
                                : undefined
                        }
                        onClick={() => toggleSlot(activeCourtPicker, idx)}
                      >
                        <span>{formatTimeSlotRange(entry.slot)}</span>
                        {entry.dayOffset === 1 && <span className="ahop-nextday-tag">NEXT DAY</span>}
                        {booked && <span className="ahop-booked-tag">BOOKED</span>}
                        {otherSessionLabel && <span className="ahop-booked-tag">IN USE</span>}
                        {passed && <span className="ahop-booked-tag">PASSED</span>}
                      </button>
                    );
                  })}
                </div>

                <button type="button" className="ahop-manual-link" onClick={() => setPickerMode('range')}>
                  Or set a start and end time instead
                </button>

                <button type="button" className="ahop-confirm ahop-nested-confirm" onClick={() => setActiveCourtPicker(null)}>
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default AdminHostOpenPlayModal;
